/**
 * useAgendaReminder — foreground reminder that fires a native browser
 * notification 30 minutes before any agenda item of the day that has a
 * `time` set.
 *
 * Scope on purpose: this hook only works while the app is OPEN on the
 * device (desktop tab or installed PWA foreground).  For real background
 * push we'd need VAPID + service worker + a backend cron — tracked as a
 * follow-up ("Push real" / option B).
 *
 * Design:
 *   • The user explicitly turns reminders ON via a bell toggle; the first
 *     flip asks `Notification.requestPermission()`.
 *   • Preference persisted in localStorage (`amc.agenda.reminders`).
 *   • Every 60s (and on mount) we:
 *       1. refetch today's agenda,
 *       2. for each item with `time`, compute the diff with "now",
 *       3. if diff is within [REMIND_MIN - WINDOW, REMIND_MIN], fire
 *          Notification(…) and remember the item id (so we never fire
 *          twice for the same event in the same day).
 *   • Fired-ids map is keyed by `${todayIso}:${itemId}` so switching
 *     days wipes the slate automatically.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const REMIND_MIN = 30;    // minutes before the event
const WINDOW_MIN = 2;     // widen the trigger window to tolerate polling drift
const POLL_MS   = 60_000; // 1 minute
const STORAGE_KEY = "amc.agenda.reminders";
const FIRED_KEY   = "amc.agenda.reminders.fired";

const todayIso = () => new Date().toISOString().slice(0, 10);

const parseItemTime = (dateIso, hhmm) => {
  // Items store date in "YYYY-MM-DD" and time in "HH:mm" (local).
  // Build the Date in local time so the diff lines up with the user's clock.
  if (!dateIso || !hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return null;
  const [y, m, d]   = dateIso.split("-").map(Number);
  const [hh, mm]    = hhmm.split(":").map(Number);
  const dt = new Date(y, m - 1, d, hh, mm, 0, 0);
  return isNaN(dt.getTime()) ? null : dt;
};

const notificationsSupported = () =>
  typeof window !== "undefined" && "Notification" in window;

const loadFiredMap = () => {
  try {
    const raw = localStorage.getItem(FIRED_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Prune entries older than today — the map should always be short.
    const today = todayIso();
    const pruned = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (k.startsWith(`${today}:`)) pruned[k] = v;
    }
    return pruned;
  } catch { return {}; }
};
const saveFiredMap = (m) => {
  try { localStorage.setItem(FIRED_KEY, JSON.stringify(m)); } catch {}
};

export default function useAgendaReminder() {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  const [permission, setPermission] = useState(() =>
    notificationsSupported() ? Notification.permission : "unsupported",
  );
  const firedRef = useRef(loadFiredMap());
  const timerRef = useRef(null);

  // Request permission + flip ON in one call.  Returns true if we end up
  // enabled (user granted), false otherwise.
  const requestEnable = useCallback(async () => {
    if (!notificationsSupported()) {
      toast.error("Este navegador não suporta notificações.");
      return false;
    }
    let perm = Notification.permission;
    if (perm === "default") {
      try { perm = await Notification.requestPermission(); } catch { perm = "denied"; }
    }
    setPermission(perm);
    if (perm !== "granted") {
      toast.error("Permissão de notificação negada. Habilite no navegador.");
      return false;
    }
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
    setEnabled(true);
    toast.success("Lembretes ativados — vou avisar 30 min antes de cada item.");
    // Small confirmation ping so the user sees the OS notification working.
    try {
      new Notification("AMC Escolas", {
        body: "Lembretes de cronograma ativados ✅",
        icon: "/icon-192.png",
        tag: "amc-reminder-confirm",
      });
    } catch {}
    return true;
  }, []);

  const disable = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, "0"); } catch {}
    setEnabled(false);
    toast("Lembretes desativados");
  }, []);

  // One-shot check: pull today's agenda and fire overdue reminders.
  const checkNow = useCallback(async () => {
    if (!enabled || permission !== "granted") return;
    let items = [];
    try {
      const r = await api.get(`/agenda?date=${todayIso()}`);
      items = r.data?.items || [];
    } catch { return; }
    const now = Date.now();
    const firstDue = REMIND_MIN - WINDOW_MIN;
    for (const it of items) {
      if (!it.time) continue;
      if (it.status === "feito" || it.status === "finalizado") continue;
      const when = parseItemTime(it.date, it.time);
      if (!when) continue;
      const diffMin = (when.getTime() - now) / 60000;
      if (diffMin < firstDue || diffMin > REMIND_MIN) continue;
      const key = `${todayIso()}:${it.id}`;
      if (firedRef.current[key]) continue;
      try {
        new Notification("⏰ Daqui 30 min no cronograma", {
          body: `${it.time} • ${it.title || "Sem título"}${it.school_name ? ` — ${it.school_name}` : ""}`,
          icon: "/icon-192.png",
          tag: `amc-reminder-${it.id}`,
          requireInteraction: false,
        });
        firedRef.current[key] = Date.now();
        saveFiredMap(firedRef.current);
      } catch {}
    }
  }, [enabled, permission]);

  useEffect(() => {
    if (!enabled || permission !== "granted") {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return;
    }
    // Run immediately, then every minute.
    checkNow();
    timerRef.current = setInterval(checkNow, POLL_MS);
    return () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
  }, [enabled, permission, checkNow]);

  return {
    supported: notificationsSupported(),
    permission,
    enabled: enabled && permission === "granted",
    requestEnable,
    disable,
  };
}
